import { Input } from '@invent/wl-ui-kit-next/ui';
import { Handshake, Search } from 'lucide-react';
import * as React from 'react';

import type { IAdminSecAdvFirmDetail, ISecAdvCatalogue } from '../../api/sec-adv-contracts';
import { formatDollars, isObject } from '../../components/sec-adv-values';
import { ITEM_7A, answeredYes, relatedPersonType } from './firm-data';
import { Figure, PILL, Panel } from './panel';
import { Dash, SourceLink, elementAt } from './register';

interface IAffiliate {
  key: string;
  name: string;
  secNumber: Nullable<string>;
  place: Nullable<string>;
  assets: Nullable<number>;
  related: boolean;
}

/**
 * A firm's Affiliates (SR-423, Phase 1): the custodians its latest filing names (Schedule D
 * 5.K(3)) under "Affiliated custodians", and the kinds of related person it reports (Item 7.A),
 * with one search over both. Firms that claim this one as an affiliate, and links to the
 * affiliates' own pages, wait for the cross-matching.
 */
export function FirmAffiliates({
  firm,
  catalogue
}: {
  firm: IAdminSecAdvFirmDetail;
  catalogue: Nullable<ISecAdvCatalogue>;
}) {
  const [find, setFind] = React.useState('');
  const words = find.trim().toLowerCase();
  const custodians = (firm.custodians?.custodians ?? []).filter(isObject).map(
    (custodian, index): IAffiliate => {
      const text = (name: string) => {
        const value = custodian[name];
        return typeof value === 'string' && value !== '' ? value : null;
      };
      const place = [text('city'), text('state'), text('country')].filter(Boolean).join(', ');
      return {
        key: text('key') ?? String(index),
        name: text('business_name') ?? text('name') ?? '–',
        secNumber: text('bd_sec_number'),
        place: place || null,
        assets: typeof custodian.assets === 'number' ? custodian.assets : null,
        related: custodian.related === true
      };
    }
  );
  const element = elementAt(catalogue, ITEM_7A);
  const related = answeredYes(firm.record, catalogue, ITEM_7A).map((name) => {
    const label = element?.attributes.find((attribute) => attribute.name === name)?.label ?? name;
    return { name, type: relatedPersonType(label) };
  });

  const shownCustodians = custodians.filter(
    (custodian) =>
      words === '' ||
      `affiliated custodian ${custodian.name} ${custodian.secNumber ?? ''} ${custodian.place ?? ''}`
        .toLowerCase()
        .includes(words)
  );
  const shownRelated = related.filter(
    (entry) => words === '' || `related person ${entry.type}`.toLowerCase().includes(words)
  );
  const none = custodians.length === 0 && related.length === 0;

  return (
    <Panel
      title="Affiliates"
      icon={Handshake}
      help={
        'Custodians named in Schedule D 5.K(3) — listed only above a 10% threshold, so the ' +
        'list is incomplete by design — and the related-person types of Form ADV Item 7.A.'
      }
    >
      {none ? (
        <p>{firm.legalName} has reported no business affiliates.</p>
      ) : (
        <>
          <p>{firm.legalName} has reported the following business affiliates.</p>
          <label className="relative block">
            <Search
              className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-muted-foreground"
              aria-hidden
            />
            <Input
              type="search"
              aria-label="Search affiliates"
              placeholder="Search by type, name or SEC number"
              value={find}
              onChange={(event) => setFind(event.target.value)}
              className="pl-8"
            />
          </label>
          {shownCustodians.length > 0 && (
            <section aria-label="Affiliated custodians" className="grid gap-2">
              <h3 className="font-medium text-[var(--color-royal-blue)]">
                Affiliated custodians{' '}
                <span className="font-normal text-muted-foreground">({shownCustodians.length})</span>
              </h3>
              <ul className="grid gap-2">
                {shownCustodians.map((custodian) => (
                  <li
                    key={custodian.key}
                    className="grid gap-3 rounded-md border border-border p-3 sm:grid-cols-[2fr_1fr_1fr_1fr]"
                  >
                    <Figure label="Name">
                      <span className="font-medium">{custodian.name}</span>
                      {custodian.related && (
                        <span className="ml-2 text-xs text-muted-foreground">Related person</span>
                      )}
                    </Figure>
                    <Figure label="SEC number">{custodian.secNumber ?? <Dash />}</Figure>
                    <Figure label="Location">{custodian.place ?? <Dash />}</Figure>
                    <Figure label="Assets held">
                      {custodian.assets !== null ? formatDollars(custodian.assets) : <Dash />}
                    </Figure>
                  </li>
                ))}
              </ul>
            </section>
          )}
          {shownRelated.length > 0 && (
            <section aria-label="Related persons" className="grid gap-2">
              <h3 className="font-medium text-[var(--color-royal-blue)]">
                Related persons{' '}
                <span className="font-normal text-muted-foreground">({shownRelated.length})</span>
              </h3>
              <ul className="flex flex-wrap gap-1.5">
                {shownRelated.map((entry) => (
                  <li key={entry.name}>
                    <SourceLink
                      firmCrd={firm.firmCrd}
                      source={`${ITEM_7A}.${entry.name}`}
                      className={`inline-block ${PILL} no-underline`}
                    >
                      {entry.type}
                    </SourceLink>
                  </li>
                ))}
              </ul>
            </section>
          )}
          {words !== '' && shownCustodians.length === 0 && shownRelated.length === 0 && (
            <p className="text-muted-foreground">No affiliate matches.</p>
          )}
        </>
      )}
    </Panel>
  );
}
